import React from "react"
import { Tel } from "@pga/pga-component-library"

import { Title } from "./styled"
import { hasNullCurrentLevel, hasLinkAndLevel } from "./utils"
import { isTerminated } from "../../../utils"
import {
  shouldShowSuspensionWarning,
  getAllActiveSuspensions,
  getSuspensionMessage,
  GPTP_MESSAGE_VARIANT,
} from "../../MembershipJourney/utils/suspensionUtils"
import { LEVEL_NAMES } from "../../MembershipJourney/utils/constants/levels"
import { useMembershipJourneyContext } from "../../MembershipJourney/MembershipJourneyContext"
import {
  useEducationPurchaseLinkProps,
  useResolveEducationPgmPurchaseUrl,
} from "../../../../utils/educationPgmPurchase"
import { flags, useFeatureFlags } from "../../../../lib/feature-flags"
import { shouldSuppressAdvancementCtas } from "../../../../utils/shouldSuppressAdvancementCtas"
import { isAcceptableProgressTerminatedStanding } from "../../../../utils/resolveAcceptableProgressStanding"

export const MessagePurchaseLink = ({
  me,
  nextLevel,
  purchaseLink,
  title: { completed, Contact },
}) => {
  const resolveEducationPgmPurchaseUrl = useResolveEducationPgmPurchaseUrl()
  const hasPurchaseLink = hasLinkAndLevel(purchaseLink, nextLevel)()
  const purchaseLinkProps = useEducationPurchaseLinkProps(
    hasPurchaseLink
      ? resolveEducationPgmPurchaseUrl(purchaseLink(me.pgaId))
      : null,
  )

  return (
    <Title>
      {completed}
      {Contact && <Contact />}
      {hasPurchaseLink && (
        <>
          <a {...purchaseLinkProps} className="pl-1">
            Purchase Level {nextLevel}
          </a>{" "}
          now to continue.
        </>
      )}
    </Title>
  )
}

export const MessageTerminated = () => (
  <Title>
    Your membership is not <u className="text-danger">current</u>. Please
    contact PGA Membership Services at <Tel phoneNumber="(800) 474-2776" />
  </Title>
)

export const AcceptableProgressTerminatedMessage = ({ currentLevelName }) => (
  <Title>
    Your membership is not current due to failure to meet Acceptable Progress in
    the PGA Professional Golf Management Program. Complete all remaining
    requirements under {currentLevelName || "your current level"}, then reapply
    before purchasing or starting the next level.
  </Title>
)

export const MessageSuspended = () => {
  const { me, pgmProgress, gptpMessageVariant, progressStanding } =
    useMembershipJourneyContext()
  const [isTerminatedNoticeEnabled = false] =
    useFeatureFlags([flags.FLAG_FEAT_TERMINATED_NOTICE]) ?? []
  const suspensions = getAllActiveSuspensions(pgmProgress)
  const resolvedGptpMessageVariant =
    gptpMessageVariant || GPTP_MESSAGE_VARIANT.SUSPENDED
  const isAcceptableProgressTerminated =
    resolvedGptpMessageVariant === GPTP_MESSAGE_VARIANT.TERMINATED ||
    isAcceptableProgressTerminatedStanding(me)

  const currentLevelKey =
    pgmProgress?.currentLevelKey ||
    pgmProgress?.mostRecentCompletedLevelKey ||
    null
  const currentLevelName = LEVEL_NAMES[currentLevelKey] || null

  if (
    isTerminatedNoticeEnabled &&
    isAcceptableProgressTerminated &&
    progressStanding?.allowNextLevelPurchase === false
  ) {
    return (
      <AcceptableProgressTerminatedMessage
        currentLevelName={currentLevelName}
      />
    )
  }

  if (suspensions.length === 0) return null

  return suspensions.map((suspensionData, index) => {
    const message = getSuspensionMessage(
      suspensionData,
      currentLevelName,
      currentLevelKey,
    )
    return message ? <Title key={index}>{message}</Title> : null
  })
}

export const MessageDefault = ({ title = {} }) => (
  <Title>{title.inProgress}</Title>
)

const Message = ({ title, nextLevel, purchaseLink }) => {
  const { me, pgmProgress, progressStanding, gptpMessageVariant } =
    useMembershipJourneyContext()
  const [isTerminatedNoticeEnabled = false] =
    useFeatureFlags([flags.FLAG_FEAT_TERMINATED_NOTICE]) ?? []
  const suppressAdvancement = shouldSuppressAdvancementCtas({
    standing: progressStanding,
    isTerminatedNoticeEnabled,
  })
  const isAcceptableProgressTerminated =
    gptpMessageVariant === GPTP_MESSAGE_VARIANT.TERMINATED ||
    isAcceptableProgressTerminatedStanding(me)
  const useAptTerminatedCopy =
    isTerminatedNoticeEnabled &&
    isAcceptableProgressTerminated &&
    progressStanding?.allowNextLevelPurchase === false

  if (useAptTerminatedCopy) {
    return <MessageSuspended />
  }

  if (suppressAdvancement) {
    return null
  }

  if (
    isTerminated(me) &&
    (!isAcceptableProgressTerminated || !isTerminatedNoticeEnabled)
  ) {
    return <MessageTerminated />
  }

  if (shouldShowSuspensionWarning(pgmProgress)) {
    return <MessageSuspended />
  }

  if (hasNullCurrentLevel(me)) {
    return <MessagePurchaseLink {...{ me, title, nextLevel, purchaseLink }} />
  }

  return <MessageDefault {...{ title, nextLevel, purchaseLink }} />
}

export default Message
